import React, { useState, useRef, useEffect, useCallback } from 'react';
import { translateAPI, decksAPI } from '../services/api';
import { useTranslate } from '../context/TranslateContext';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import {
  ArrowLeftRight, Loader, BookmarkPlus, Volume2,
  History, X, ChevronDown, Plus, Trash2, MessageSquare
} from 'lucide-react';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fa', label: 'Persian' },
];

const LANG_VOICES = { en: 'en-US', fi: 'fi-FI', fa: 'fa-IR' };

export default function Translate() {
  const {
    text, setText, sourceLang, setSourceLang,
    targetLang, setTargetLang, result, setResult,
    history, saveToHistory, loadFromHistory, clearHistory, deleteFromHistory,
  } = useTranslate();

  const [loading, setLoading] = useState(false);
  const [decks, setDecks] = useState([]);
  const [saving, setSaving] = useState(null);
  const [showDeckPicker, setShowDeckPicker] = useState(null); // {index, x, y, above}
  const [showHistory, setShowHistory] = useState(false);
  const [wordPopup, setWordPopup] = useState(null); // {word, x, y}
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [wordExamples, setWordExamples] = useState([]);
  const [speaking, setSpeaking] = useState(null);
  const popupRef = useRef(null);

  // 1404
  const [phrasePopup, setPhrasePopup] = useState(null);
  const phrasePopupRef = useRef(null);
  //

  // Load decks once
  useEffect(() => {
    decksAPI.list().then(r => setDecks(r.data)).catch(() => {});
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setWordPopup(null);
        setWordExamples([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 1404
  useEffect(() => {
    const handler = (e) => {
      if (phrasePopupRef.current && !phrasePopupRef.current.contains(e.target)) {
        setPhrasePopup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  //

  const swapLangs = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setResult(null);
  };

  const handleTranslate = async () => {
    if (!text.trim()) { toast.error('Please enter some text'); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await translateAPI.translate(text, sourceLang, targetLang);
      setResult(r.data);
      saveToHistory(text, sourceLang, targetLang, r.data);
    } catch {
      toast.error('Translation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Text-to-speech
  const speak = (word, lang) => {
    if (!window.speechSynthesis) { toast.error('TTS not supported in this browser'); return; }
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(word);
    utter.lang = LANG_VOICES[lang] || 'en-US';
    utter.onstart = () => setSpeaking(word);
    utter.onend = () => setSpeaking(null);
    utter.onerror = () => setSpeaking(null);
    window.speechSynthesis.speak(utter);
  };

  const handleWordClick = useCallback(async (word, e, wordLang) => {
    const cleaned = word.replace(/[^a-zA-ZäöåÄÖÅ\u0600-\u06FF\u00C0-\u024F\u4E00-\u9FFF]/g, '');
    if (cleaned.length < 2) return;
    const rect = e.target.getBoundingClientRect();
    const popupH = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const y = spaceBelow >= popupH
      ? rect.bottom + 6
      : Math.max(8, rect.top - popupH - 6);
    const x = Math.min(Math.max(rect.left, 8), window.innerWidth - 268);
    const translateTo = wordLang === sourceLang ? targetLang : sourceLang;
    setWordPopup({ word: cleaned, wordLang, translateTo, x, y, translation: null });
    setWordExamples([]);
    setLoadingExamples(true);
    try {
      const [transRes, exRes] = await Promise.allSettled([
        translateAPI.translate(cleaned, wordLang, translateTo),
        translateAPI.getExamples(cleaned, wordLang, 'B1'),
      ]);
      const wordTranslation = transRes.status === 'fulfilled'
        ? transRes.value.data.translated_text : '';
      const examples = exRes.status === 'fulfilled'
        ? (exRes.value.data.examples || []) : [];
      setWordPopup(prev => prev ? { ...prev, translation: wordTranslation } : prev);
      setWordExamples(examples);
    } catch {
      setWordExamples([]);
    } finally {
      setLoadingExamples(false);
    }
  }, [sourceLang, targetLang]);

  // 1404
  const handleTextSelect = useCallback((e, lang) => {
    const selection = window.getSelection();
    const phrase = selection?.toString().trim();
    if (!phrase || phrase.length < 3 || phrase.split(' ').length < 2) return;
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const popupH = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const y = spaceBelow >= popupH ? rect.bottom + 6 : Math.max(8, rect.top - popupH - 6);
    const x = Math.min(Math.max(rect.left, 8), window.innerWidth - 268);
    const translateTo = lang === sourceLang ? targetLang : sourceLang;
    setPhrasePopup({ phrase, x, y, translateTo, phraseLang: lang, translation: null, loading: true, showDeck: false });
    translateAPI.translate(phrase, lang, translateTo)
      .then(r => setPhrasePopup(prev => prev ? { ...prev, translation: r.data.translated_text, loading: false } : prev))
      .catch(() => setPhrasePopup(prev => prev ? { ...prev, loading: false } : prev));
}, [sourceLang, targetLang]);
  //

  const saveToDeck = async (deckId, vocab) => {
    setSaving(vocab.word);
    try {
      await decksAPI.addFlashcard(deckId, {
        word: vocab.word,
        translation: vocab.translation,
        example_sentence: vocab.example_sentence,
        cefr_level: vocab.cefr_level,
        source_lang: targetLang,
        target_lang: sourceLang,
        deck_id: deckId,
      });
      toast.success(`"${vocab.word}" saved to deck ✓`);
      setShowDeckPicker(null);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  // Save word from popup — now includes translation
  const saveWordToDeck = async (deckId, word) => {
    if (!wordPopup) return;
    setSaving(word);
    try {
      await decksAPI.addFlashcard(deckId, {
        word,
        translation: wordPopup.translation || '',
        example_sentence: wordExamples[0] || '',
        cefr_level: 'B1',
        source_lang: wordPopup.wordLang,
        target_lang: wordPopup.translateTo,
        deck_id: deckId,
      });
      toast.success(`"${word}" saved ✓`);
      setWordPopup(null);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  // 1404
  const savePhraseToDeck = async (deckId) => {
    if (!phrasePopup) return;
    setSaving(phrasePopup.phrase);
    try {
      await decksAPI.addFlashcard(deckId, {
        word: phrasePopup.phrase,
        translation: phrasePopup.translation || '',
        example_sentence: '',
        cefr_level: 'B1',
        source_lang: phrasePopup.phraseLang,
        target_lang: phrasePopup.translateTo,
        deck_id: deckId,
      });
      toast.success('Phrase saved ✓');
      setPhrasePopup(null);
      window.getSelection()?.removeAllRanges();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };
  //

  // Render clickable words — lang identifies which box they came from
  const renderClickableText = (txt, lang) => {
    if (!txt) return null;
    const words = txt.split(/(\s+)/);
    return words.map((w, i) =>
      w.trim() ? (
        <span key={i} className="clickable-word" onClick={(e) => handleWordClick(w, e, lang)}>{w}</span>
      ) : w
    );
  };

  const langLabel = (code) => LANGS.find(l => l.code === code)?.label || code;
  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1>Smart Translate</h1>
          <p>Translate text · click any word · save to deck</p>
        </div>
        <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => setShowHistory(s => !s)}>
          <History size={15} /> History {history.length > 0 && `(${history.length})`}
        </button>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="card fade-in" style={{ marginBottom: '1.5rem', borderColor: 'var(--border-glow)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Translation History</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {history.length > 0 && (
                <button className="btn btn-danger" style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.78rem' }}
                  onClick={() => { clearHistory(); setShowHistory(false); }}>
                  <Trash2 size={12} /> Clear
                </button>
              )}
              <button className="btn btn-icon" onClick={() => setShowHistory(false)}><X size={14} /></button>
            </div>
          </div>
          {history.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>No history yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 280, overflowY: 'auto' }}>
              {history.map(entry => (
                <div key={entry.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div className="history-item" style={{ flex: 1 }}
                    onClick={() => { loadFromHistory(entry); setShowHistory(false); }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--accent-soft)', fontWeight: 600 }}>
                        {langLabel(entry.sourceLang)} → {langLabel(entry.targetLang)}
                      </span>
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-faint)' }}>{formatTime(entry.timestamp)}</span>
                    </div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.85rem' }}>
                      {entry.text.slice(0, 60)}{entry.text.length > 60 ? '…' : ''}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.15rem' }}>
                      {entry.translated_text?.slice(0, 60)}{entry.translated_text?.length > 60 ? '…' : ''}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteFromHistory(entry.id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: '0.4rem', borderRadius: 6, flexShrink: 0, transition: 'color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
                    title="Delete this entry"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Language selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select value={sourceLang} onChange={e => setSourceLang(e.target.value)}>
          {LANGS.filter(l => l.code !== targetLang).map(l => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
        <button className="btn btn-icon" onClick={swapLangs} title="Swap languages">
          <ArrowLeftRight size={15} />
        </button>
        <select value={targetLang} onChange={e => setTargetLang(e.target.value)}>
          {LANGS.filter(l => l.code !== sourceLang).map(l => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      {/* Two-panel layout: source | translation */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1rem', alignItems: 'start' }}>

        {/* SOURCE BOX */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {langLabel(sourceLang)}
            </span>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {result && (
                <>
                  <button className="btn btn-icon" style={{ width: 28, height: 28 }}
                    onClick={() => speak(text, sourceLang)} title="Read aloud">
                    <Volume2 size={12} />
                  </button>
                  <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                    onClick={() => setResult(null)}>
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>

          {result ? (
            <div>
              <p style={{
                fontSize: '0.92rem', lineHeight: 1.9, color: 'var(--text-primary)',
                userSelect: 'text', wordBreak: 'break-word',
                direction: sourceLang === 'fa' ? 'rtl' : 'ltr',
                textAlign: sourceLang === 'fa' ? 'right' : 'left',
              }}
              onMouseUp={(e) => handleTextSelect(e, sourceLang)}
              >
                {renderClickableText(text, sourceLang)}
              </p>
              <p style={{ fontSize: '0.68rem', color: 'var(--text-faint)', marginTop: '0.8rem' }}>
                💡 Click any word to translate &amp; save
              </p>
                            
            </div>
          ) : (
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={`Enter ${langLabel(sourceLang)} text here…`}
              style={{
                minHeight: '160px', background: 'transparent',
                border: 'none', color: 'var(--text-primary)', fontFamily: 'inherit',
                fontSize: '0.92rem', resize: 'vertical', outline: 'none', lineHeight: 1.85,
                width: '100%',
              }}
              onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleTranslate(); }}
            />
          )}
        </div>

        {/* TRANSLATION BOX */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', borderColor: result ? 'var(--border-glow)' : 'var(--border)', opacity: result ? 1 : 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--accent-soft)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {langLabel(targetLang)}
            </span>
            {result && (
              <button className="btn btn-icon" style={{ width: 28, height: 28 }}
                onClick={() => speak(result.translated_text, targetLang)} title="Read aloud">
                <Volume2 size={12} />
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', padding: '1rem 0' }}>
              <Loader size={16} className="spin" /> Translating…
            </div>
          ) : result ? (
            <div>
              <p style={{
                fontSize: '0.92rem', lineHeight: 1.9, color: 'var(--text-primary)',
                userSelect: 'text', wordBreak: 'break-word',
                direction: targetLang === 'fa' ? 'rtl' : 'ltr',
                textAlign: targetLang === 'fa' ? 'right' : 'left',
              }}
              onMouseUp={(e) => handleTextSelect(e, targetLang)}
              >
                {renderClickableText(result.translated_text, targetLang)}
              </p>
              <p style={{ fontSize: '0.68rem', color: 'var(--text-faint)', marginTop: '0.7rem' }}>
                💡 Click any word · select phrase to save to translate &amp; save
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--text-faint)', fontSize: '0.85rem', padding: '1rem 0' }}>
              Translation will appear here…
            </p>
          )}
        </div>
      </div>

      {/* Translate button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <button className="btn btn-primary" style={{ width: 'auto', paddingInline: '2.5rem' }}
          onClick={handleTranslate} disabled={loading}>
          {loading ? <><Loader size={14} className="spin" /> Translating…</> : result ? 'Re-translate' : 'Translate'}
        </button>
      </div>

      {/* Vocabulary */}
      {result && result.vocabulary?.length > 0 && (
        <div className="fade-in">
          <h3 style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
            Key Vocabulary ({result.vocabulary.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
            {result.vocabulary.map((vocab, i) => (
              <div key={i} className="card card-sm fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontSize: '1rem' }}>{vocab.word}</span>
                      <button className="btn btn-icon" style={{ width: 26, height: 26, padding: 0 }}
                        onClick={() => speak(vocab.word, targetLang)} title="Pronounce">
                        <Volume2 size={11} style={{ color: speaking === vocab.word ? 'var(--accent-soft)' : undefined }} />
                      </button>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>— {vocab.translation}</span>
                      <span className={`badge badge-${(vocab.cefr_level || 'b1').toLowerCase()}`}>
                        {vocab.cefr_level}
                      </span>
                    </div>
                    {vocab.example_sentence && (
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.6 }}>
                        "{vocab.example_sentence}"
                      </p>
                    )}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <button className="btn btn-icon" title="Save to deck"
                      onClick={(e) => {
                        if (showDeckPicker?.index === i) { setShowDeckPicker(null); return; }
                        const rect = e.currentTarget.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        const above = spaceBelow < 200;
                        setShowDeckPicker({
                          index: i,
                          x: rect.right - 180,
                          y: above ? rect.top - 8 : rect.bottom + 6,
                          above,
                        });
                      }}
                      disabled={saving === vocab.word}>
                      {saving === vocab.word ? <Loader size={13} className="spin" /> : <BookmarkPlus size={13} />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Deck picker portal - fixed position, never clipped */}
      {showDeckPicker && (
        <DeckPickerPortal
          decks={decks}
          x={showDeckPicker.x}
          y={showDeckPicker.y}
          above={showDeckPicker.above}
          onSelect={(id) => {
            const vocabItem = result?.vocabulary?.[showDeckPicker.index];
            if (vocabItem) saveToDeck(id, vocabItem);
            setShowDeckPicker(null);
          }}
          onClose={() => setShowDeckPicker(null)}
        />
      )}

      {wordPopup && (
        <div ref={popupRef} className="word-popup" style={{
          top: Math.min(wordPopup.y, window.innerHeight - 320),
          left: Math.min(Math.max(wordPopup.x, 8), window.innerWidth - 268),
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>{wordPopup.word}</span>
              <button className="btn btn-icon" style={{ width: 26, height: 26, padding: 0 }}
                onClick={() => speak(wordPopup.word, wordPopup.wordLang)}>
                <Volume2 size={11} />
              </button>
            </div>
            <button className="btn btn-icon" style={{ width: 26, height: 26, padding: 0 }}
              onClick={() => { setWordPopup(null); setWordExamples([]); }}>
              <X size={12} />
            </button>
          </div>

          {/* Translation */}
          <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.7rem', background: 'var(--bg-deep)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
              {langLabel(wordPopup.translateTo)} translation
            </div>
            {wordPopup.translation === null ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Loader size={11} className="spin" /> Translating…
              </div>
            ) : wordPopup.translation ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: 'var(--accent-soft)', fontWeight: 700, fontSize: '0.95rem' }}>{wordPopup.translation}</span>
                <button className="btn btn-icon" style={{ width: 22, height: 22, padding: 0 }}
                  onClick={() => speak(wordPopup.translation, wordPopup.translateTo)}>
                  <Volume2 size={10} />
                </button>
              </div>
            ) : (
              <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem' }}>—</span>
            )}
          </div>

          {/* Examples */}
          <div style={{ marginBottom: '0.8rem' }}>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              Example sentences ({langLabel(wordPopup.wordLang)})
            </div>
            {loadingExamples ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Loader size={12} className="spin" /> Loading…
              </div>
            ) : wordExamples.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {wordExamples.map((ex, i) => (
                  <p key={i} style={{ fontSize: '0.79rem', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5 }}>
                    {i + 1}. {ex}
                  </p>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '0.78rem', color: 'var(--text-faint)' }}>No examples found</p>
            )}
          </div>

          {/* Save to deck */}
          <div style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
            Save to Deck
          </div>
          <DeckDropdown decks={decks} onSelect={(id) => saveWordToDeck(id, wordPopup.word)} onClose={() => {}} inline />
        </div>
      )}
      {phrasePopup && (
        <div ref={phrasePopupRef} className="word-popup" style={{
          top: Math.min(phrasePopup.y, window.innerHeight - 220),
          left: Math.min(Math.max(phrasePopup.x, 8), window.innerWidth - 268),
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              Selected phrase
            </span>
            <button className="btn btn-icon" style={{ width: 26, height: 26, padding: 0 }}
              onClick={() => setPhrasePopup(null)}>
              <X size={12} />
            </button>
          </div>

          <p style={{ fontSize: '0.82rem', color: 'var(--accent-soft)', marginBottom: '0.7rem', fontStyle: 'italic' }}>
            "{phrasePopup.phrase}"
          </p>

          <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.7rem', background: 'var(--bg-deep)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
              Translation
            </div>
            {phrasePopup.loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Loader size={11} className="spin" /> Translating…
              </div>
            ) : phrasePopup.translation ? (
              <span style={{ color: 'var(--text-primary)', fontSize: '0.88rem' }}>{phrasePopup.translation}</span>
            ) : (
              <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem' }}>—</span>
            )}
          </div>

          <div style={{ fontSize: '0.67rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
            Save to Deck
          </div>
          <DeckDropdown decks={decks} onSelect={(id) => savePhraseToDeck(id)} onClose={() => {}} inline />
        </div>
      )}
    </Layout>
  );
}

function DeckPickerPortal({ decks, x, y, above, onSelect, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={ref} style={{
      position: 'fixed',
      left: Math.max(8, Math.min(x, window.innerWidth - 196)),
      ...(above ? { bottom: window.innerHeight - y } : { top: y }),
      zIndex: 500,
      background: 'var(--bg-raised)',
      border: '1.5px solid var(--border-glow)',
      borderRadius: 'var(--radius)',
      minWidth: 188,
      padding: '0.4rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      animation: 'fadeIn 0.15s ease',
    }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.3rem 0.6rem 0.5rem' }}>
        Save to deck
      </div>
      {decks.length === 0 ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0.6rem' }}>
          Create a deck first
        </div>
      ) : (
        decks.map(d => (
          <button key={d.id} onClick={() => onSelect(d.id)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: 'none', border: 'none', color: 'var(--text-primary)',
            padding: '0.45rem 0.6rem', cursor: 'pointer', borderRadius: 6,
            fontSize: '0.83rem', fontFamily: 'inherit', transition: 'background 0.13s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            {d.title}
          </button>
        ))
      )}
    </div>
  );
}

function DeckDropdown({ decks, onSelect, onClose }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      {decks.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', padding: '0.3rem 0' }}>
          Create a deck first
        </div>
      ) : (
        decks.map(d => (
          <button key={d.id} onClick={() => onSelect(d.id)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: 'var(--bg-deep)', border: '1px solid var(--border)',
            color: 'var(--text-primary)', padding: '0.4rem 0.6rem',
            cursor: 'pointer', borderRadius: 6,
            fontSize: '0.81rem', fontFamily: 'inherit', transition: 'border-color 0.13s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--accent-soft)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
          >
            {d.title}
          </button>
        ))
      )}
    </div>
  );
}